/**
 * 被投诉方选择器 —— 手机端与 PC 端**共用这一份**。
 *
 * 为什么共用：投诉表单是「两个入口、同一件事」，规则已经统一在 `util/complaint-rules.ts`，
 * 这一段交互（怎么查、查不到怎么办、选中长什么样）同样不许有两份实现。
 * 样式走 `styles/respondent.css`，用自己的类名前缀 `rp-`，两端各自的表单样式都不会串。
 *
 * ★★ 2026-08-27 主公改口径：被投诉方从「人员／机构二选一」改成**两条互相独立的线**，
 *   与管理后台一致（见 `packages/web/src/drawers/CaseDrawers.tsx` 的「I2」）。
 *   只投人 → 只用上面那块；只投机构 → 只用下面那块；两者都投 → 两块各查各的。
 *   **留空表示「本案没投这一方」**，和「查不到」是两码事，所以界面上要把这句话说出来——
 *   改前那个 tab 二选一，市民根本没法表达「这个人和他公司我都要投」。
 *
 * ★★ 2026-08-27（同日晚）主公令：查询与选择的过程**从内联列表改成底部抽屉**。
 *   改前每条线都是「搜索框 ＋ 结果列表（自带 260px 滚动）＋ 手工兜底入口」直接摊在表单里，
 *   两条线一摆，表单被撑长一大截，而且页面里要滚两层（表单滚一层、结果列表再滚一层）——
 *   手机上极容易滚错那一层。改后表单里每条线只剩**一行像表单字段的触发行**，
 *   查询过程搬进浮层，选完即收、值回填。
 *
 *   ★ 浮层形态随断点变，但**只有一份实现**（`components/PickerSheet.tsx`）：
 *     · 窄屏（手机／手机壳内）＝底部抽屉，从下往上升；
 *     · 宽屏（PC 表单 `pc/PcForm.tsx`，要挂协会官网）＝**居中弹窗**。
 *       1280px 宽的页面从底下升起一条抽屉很怪，那是手机的手势语言。
 *     层级、锁滚、Esc、焦点、安全区这些容易做漏的事全在 `PickerSheet.tsx` 里，
 *     那个文件的头注释写了每一条的理由，改交互前先读。
 *
 *   ★ 组件对外 API 一个字没动（`person/org/onPerson/onOrg`），
 *     `pages/Submit.tsx` 与 `pc/PcForm.tsx` 因此**一行都不用改**。
 *
 * ★ 三条来自协会接口实测的硬约束，改交互前先读（2026-08-27 实测）：
 *   ① 人员**姓名与 CD 编号都能查**。姓名得写全，写一半基本查不到；编号是唯一键，
 *      一次就能定位到人，所以提示语把编号排在前面。
 *      （2026-09-06 更正：原文写的是「只能按姓名查，查不了 CD 编号」——那是 08-25
 *      的实测结论，主公点名复测后确认协会侧已修好，真编号回查 20/20 精确命中。）
 *   ② **结果会截断**：查「秦晓」协会侧命中 22 条、接口只返回 5 条；
 *      机构查「成都」命中 9781 条。所以截断时**必须**提示用户缩小范围——
 *      默不作声地只摆前几条，等于告诉他「名录里就这些人」，那是假状态。
 *   ③ 因此「查不到就手工填」不是备选路径，是**刚需**，入口要一直在，不能藏在深处——
 *      抽屉里它是**常驻底栏**，不管查没查、查到没查到，它都在同一个位置。
 *
 * ★ 搜索仍然**按钮触发，不做输入即搜**：协会接口一次 7 秒起步，边输边查会把它打爆。
 *
 * ★ 手工填写的绝不补编号：编号有无＝「这条是不是协会名录核对过的」的唯一判据。
 *   受理人员靠它决定要不要直接信这条信息。给手工填的编个号，是往案卷里掺假。
 *
 * ★★ 人员带出来的**所属机构只在这里显示，不进提交体**（主公 2026-08-27 定「甲」）：
 *   协会人员查询接口只给机构名、不给 orgId，写进「被投诉机构」就是个有名无号的假凭据，
 *   后台会据此把机构标成非会员——错误结论。它在界面上的唯一用处是让市民确认选对了人
 *   （「秦晓东（所属：某某公司）」）。落体在 `util/complaint-rules.ts` 的 `buildSubmitBody`。
 */

import { useCallback, useRef, useState } from 'react';
import { creditOrgSearch, creditSearchV2 } from '../api/credit';
import { PickerSheet } from './PickerSheet';
import { ChevronRight } from './Icons';
import type { RespondentOrgPick, RespondentPersonPick } from '../util/complaint-rules';
import '../styles/respondent.css';

/** 名录里的一条候选。两条线共用这个形状，差别只在 `code`/`extra` 各装什么。 */
interface Cand {
  key: string;
  name: string;
  /** 协会编号：人员＝CD/ZL 号，机构＝orgId 转字符串。接口没给就是空串，**不许自己造** */
  code: string;
  /** 副信息，**仅供显示**：人员＝所属机构，机构＝空 */
  extra: string;
}

/**
 * 一条线定下来的结果（组件内部形状）。
 * 两条线的交互完全一样，所以只写一个 `DirectoryLine`，
 * 由外面把它映射成 `RespondentPersonPick` / `RespondentOrgPick` —— 交互一份实现，
 * 契约字段各归各的，不会串成「机构那块填出个人员编号」。
 */
interface LineValue {
  fromDirectory: boolean;
  name: string;
  code: string;
  extra: string;
}

type LineKind = 'person' | 'org';

const LINE_META: Record<
  LineKind,
  {
    title: string;
    /** 抽屉标题 */
    sheetTitle: string;
    /**
     * 线名右侧那一行小字。
     *
     * ★ 2026-09-03 协会令：原文是两行式的长提示（「没有要投诉的具体人员就留空」／
     *   「没有要投诉的机构就留空」），协会嫌长，压简成「投诉人员请填」／「投诉企业请填」。
     *   ★ 但**口径一个字没变**：留空仍然表示「本案没投这一方」（见文件头 2026-08-27 那段），
     *     外层「至少填一项」也**原样保留**、不许改成「必填」（理由见 `pages/Submit.tsx`
     *     被投诉方那一段：写「必填」会被理解成两块都得填）。
     *     压掉的只是把这层意思**说全**的那半句，不是把它取消。
     */
    blankNote: string;
    /** 表单里那一行触发行未选时的占位 */
    triggerPh: string;
    ph: string;
    hint: string;
    manualPh: string;
    kindTag: string;
    /**
     * 这条线的编号**要不要摆给市民看**。
     * 人员的 CD/ZL 号是他证上印着的号，摆出来能帮他确认"是不是这个人"；
     * 机构的 orgId 是协会库里的**内部序号**，市民没见过、对不上任何东西，
     * 摆出来只是一串噪声——所以机构线只显示名字。编号照样带进提交体，只是不露脸。
     */
    showCode: boolean;
  }
> = {
  person: {
    title: '被投诉人员',
    sheetTitle: '选择被投诉人员',
    blankNote: '投诉人员请填',
    triggerPh: '点击选择被投诉人员',
    ph: '输入姓名或 CD 编号',
    /* 2026-09-06：编号查询通了（见文件头 ①）。编号写在前面是有意的 ——
       它是唯一键，一次定位到人；姓名要写全才查得到，还可能重名。 */
    hint: '可输入经纪人姓名，或直接输入 CD 编号（如 CD381365）。用姓名查请写完整。',
    manualPh: '填写被投诉人姓名',
    kindTag: '人员',
    showCode: true,
  },
  /* ★★ 2026-09-04 F4：**被投诉方这条线**露给市民的字一律由「机构」改为「企业」
     （协会口径，清单原话就是「投诉企业请填」）。改前这一块自相矛盾：
     小标签写「投诉企业请填」、字段标题写「被投诉机构」，肉眼可见地打架。
     ★★ 只改这几个**显示串**。红线（碰一个就算改错）：
       · 提交体键名 `被投诉机构` / `被投诉机构编号` —— 一个字不许动（见 `buildSubmitBody`）；
       · 契约类型 `RespondentOrgPick`、`LineKind` 的 `'org'`、CSS 类 `rp-*`、路由、接口名 —— 全不动；
       · **诚信曝光那条线的「机构」不动** —— 那里的「机构名录／机构详情／机构类型」指的是
         协会名录这个东西本身，不是「被投诉的那家企业」，改了反而与协会的说法对不上。 */
  org: {
    title: '被投诉企业',
    sheetTitle: '选择被投诉企业',
    blankNote: '投诉企业请填',
    triggerPh: '点击选择被投诉企业',
    ph: '输入企业名称',
    hint: '输入企业名称中较特别的几个字，范围越小越容易找到。',
    manualPh: '填写企业名称',
    kindTag: '企业',
    showCode: false,
  },
};

function DirectoryLine({
  kind,
  value,
  onChange,
}: {
  kind: LineKind;
  value: LineValue | null;
  onChange: (v: LineValue | null) => void;
}) {
  /** 抽屉开着没有 */
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  /** 空串＝还没查过；非空＝这一次查询的失败原因 */
  const [err, setErr] = useState('');
  /** null＝还没查过（与"查了但一条都没有"是两回事，不能混） */
  const [list, setList] = useState<Cand[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  /** 抽屉里是否切到了手工填写 */
  const [manual, setManual] = useState(false);
  const [manualName, setManualName] = useState('');
  /**
   * 这条线在页面上的位置。抽屉靠它就近找 `.screen`（手机壳）与 `.scroll`（滚动容器），
   * 从而决定"挂在哪、锁谁的滚动"。触发行选中后会被换成卡片，所以锚点挂在**外层容器**上，
   * 它任何时候都在。
   */
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /** 稳定引用：抽屉的按键监听按它决定要不要重挂，别每次渲染都换一个新函数 */
  const closeSheet = useCallback(() => setOpen(false), []);

  const meta = LINE_META[kind];

  async function doSearch() {
    const key = q.trim();
    if (!key) {
      // ★ 2026-09-04 F4：机构线的空词提示同步改「企业」（这句直接显示在抽屉里）
      setErr(kind === 'person' ? '请先输入经纪人姓名或 CD 编号' : '请先输入企业名称');
      return;
    }
    setBusy(true);
    setErr('');
    setList(null);
    setTruncated(false);
    try {
      if (kind === 'person') {
        const r = await creditSearchV2(key, 20);
        setTruncated(!!r.truncated);
        setList(
          r.items.map((it, i) => ({
            key: `${it.userId || 0}-${i}`,
            name: it.name ?? '',
            code: it.cdNumber ?? '',
            // 所属机构只落进 extra（显示用）。**接口没有 orgId，所以它永远成不了机构凭据**
            extra: it.orgName ?? '',
          })),
        );
      } else {
        const r = await creditOrgSearch(key, 1, 20);
        setTruncated(!!r.truncated);
        setList(
          r.items.map((it, i) => ({
            key: `${it.orgId || 0}-${i}`,
            name: it.orgName ?? '',
            // ★ 机构接口是给 orgId 的，这里必须带上：丢掉它，后台就会因「有名无号」
            //   把市民明明从协会机构库选的机构标成非会员
            code: it.orgId ? String(it.orgId) : '',
            extra: '',
          })),
        );
      }
    } catch (e) {
      // 取不到就说取不到，**不拿本地数据顶替**——市民端红线
      setErr(e instanceof Error ? e.message : '没能查到，请稍后重试');
    } finally {
      setBusy(false);
    }
  }

  /** 选中名录里的一条 → 回填并**收起抽屉** */
  function pick(c: Cand) {
    onChange({ fromDirectory: true, name: c.name, code: c.code, extra: c.extra });
    setOpen(false);
    setManual(false);
    setList(null);
  }

  /** 手工填写落定 → 回填并收起。编号恒空，不许为了界面好看补一个号。 */
  function commitManual() {
    const t = manualName.trim();
    if (!t) return;
    onChange({ fromDirectory: false, name: t, code: '', extra: '' });
    setOpen(false);
    setManual(false);
  }

  /** 打开抽屉。手工填过的把原名带进去接着改，省得重敲一遍。 */
  function openSheet(fresh: boolean) {
    if (fresh) {
      setManual(false);
      setManualName('');
    }
    setErr('');
    setOpen(true);
  }

  /**
   * 「重选」＝清掉当前这一条并立刻开抽屉重挑。
   * 清掉是有意的：留空在新口径下是**一个明确的意思**（本案没投这一方），
   * 所以用户按了重选又直接关掉抽屉，落在"留空"上是对的，不是丢数据。
   */
  function resetAndOpen() {
    onChange(null);
    setManualName('');
    setList(null);
    setTruncated(false);
    openSheet(true);
  }

  const picked = Boolean(value && value.name.trim());

  /* ---------- 抽屉正文：查询 → 结果，或手工填写 ---------- */
  const sheetBody = manual ? (
    <div className="rp-sheet-body">
      <input
        className="rp-input"
        autoFocus
        value={manualName}
        placeholder={meta.manualPh}
        aria-label={`手工填写${meta.title}`}
        onChange={(e) => setManualName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitManual();
          }
        }}
      />
      <p className="rp-hint">手工填写的信息未经协会名录核对，受理时会由工作人员再次核实。</p>
    </div>
  ) : (
    <>
      <div className="rp-sheet-body">
        <div className="rp-search">
          <input
            className="rp-input rp-q"
            value={q}
            placeholder={meta.ph}
            aria-label={`${meta.title}名录查询`}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void doSearch();
              }
            }}
          />
          {/* 按钮触发，不做输一个字就查——协会接口一次要 7 秒起步，边输边查会把它打爆 */}
          <button type="button" className="rp-go" disabled={busy} onClick={() => void doSearch()}>
            {busy ? '查询中' : '搜索'}
          </button>
        </div>
        <p className="rp-hint">{meta.hint}</p>
        {err ? <p className="rp-err">{err}</p> : null}
      </div>

      <div className="rp-sheet-scroll">
        {/* 一次要等 7 秒起步，这段时间里结果区不能是一片空白——那看着像点了没反应 */}
        {busy ? (
          <p className="rp-busy">
            <span className="rp-spin" aria-hidden="true" />
            正在查询，请稍候…
          </p>
        ) : list ? (
          list.length ? (
            <>
              <ul className="rp-list">
                {list.map((c) => (
                  <li key={c.key}>
                    <button type="button" className="rp-item" onClick={() => pick(c)}>
                      <b>{c.name}</b>
                      {/* 所属机构摆在这里，是为了让市民认出「是不是这个秦晓东」；
                          机构的内部序号不摆（见 `showCode`），摆了也是一串对不上的数字 */}
                      {(meta.showCode && c.code) || c.extra ? (
                        <span>
                          {[meta.showCode ? c.code : '', c.extra ? `所属：${c.extra}` : '']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
              {/* 截断必须说：只摆前几条却不吭声，等于告诉他"名录里就这些人"。
                  但**不报条数**——市民要的是「怎么找到」，不是一串统计数字 */}
              {truncated ? (
                <p className="rp-hint rp-hint--trunc">
                  符合的结果较多，这里只显示了一部分。把名称写得更完整可以更快找到。
                </p>
              ) : null}
            </>
          ) : (
            <p className="rp-empty">没有找到符合的{meta.kindTag}。</p>
          )
        ) : null}
      </div>
    </>
  );

  /* ---------- 抽屉底栏：手工兜底常驻，不管查没查都在同一个位置 ---------- */
  const sheetFooter = manual ? (
    <>
      <button
        type="button"
        className="rp-use"
        disabled={!manualName.trim()}
        onClick={commitManual}
      >
        使用这个名称
      </button>
      <button type="button" className="rp-manual-go" onClick={() => setManual(false)}>
        返回名录搜索
      </button>
    </>
  ) : (
    <button
      type="button"
      className="rp-manual-go"
      onClick={() => {
        setManualName(value && !value.fromDirectory ? value.name : '');
        setManual(true);
      }}
    >
      名录里没找到？点这里手工填写
    </button>
  );

  return (
    <div className="rp-body" ref={wrapRef}>
      {picked && value ? (
        /* ---------- 已选定：只摆结论 ＋ 一个「重选」 ----------
           ★ 名录选中（蓝）与手工填写（琥珀）两种卡片的样式与文案原封不动，
             它们是受理人员判断「这条信不信得过」的凭据，不随交互形态变。 */
        (() => {
          // 副行：编号 ＋ 所属机构。所属机构**只在这里露脸**，不进提交体；
          // 机构的内部序号不摆（`showCode`），但它照样在提交体里
          const sub = [
            meta.showCode ? value.code.trim() : '',
            value.extra.trim() ? `所属：${value.extra.trim()}` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div className={value.fromDirectory ? 'rp-picked' : 'rp-picked rp-picked--manual'}>
              <div className="rp-picked-main">
                <b>{value.name}</b>
              </div>
              {sub ? <p className="rp-picked-sub">{sub}</p> : null}
              {/* 手工填的必须自报家门，否则受理人员会误当成协会核对过的 */}
              {value.fromDirectory ? null : <p className="rp-warn">手工填写，未在协会名录中核对</p>}
              <button type="button" className="rp-reset" onClick={resetAndOpen}>
                重选
              </button>
            </div>
          );
        })()
      ) : (
        /* ---------- 未选定：一行**像表单字段的触发行**，点开才进查询 ---------- */
        <button
          type="button"
          className="rp-trigger"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => openSheet(false)}
        >
          <span className="rp-trigger-ph">{meta.triggerPh}</span>
          <ChevronRight size={11} stroke="#8A93A3" />
        </button>
      )}

      {/* 抽屉**常挂**（由 `open` 控制显隐）：收起动画归它自己播完再卸载，
          这样「点中一条」和「点遮罩／按 Esc／按关闭」收起来是同一个样子。
          写成 `{open ? <PickerSheet/> : null}` 就会变成选中时啪一下消失、其余时候有动画。 */}
      <PickerSheet
        open={open}
        title={meta.sheetTitle}
        anchor={wrapRef.current}
        autoFocusField=".rp-q"
        onClose={closeSheet}
        footer={sheetFooter}
      >
        {sheetBody}
      </PickerSheet>
    </div>
  );
}

/**
 * 两条线一起摆出来。两个入口（手机 `pages/Submit.tsx`、PC `pc/PcForm.tsx`）
 * 都直接用这一个组件，**不在页面里另拼一套**。
 */
export function RespondentPicker({
  person,
  org,
  onPerson,
  onOrg,
}: {
  person: RespondentPersonPick | null;
  org: RespondentOrgPick | null;
  onPerson: (v: RespondentPersonPick | null) => void;
  onOrg: (v: RespondentOrgPick | null) => void;
}) {
  return (
    <div className="rp">
      {/* ★ 2026-09-03 协会令「被投诉方简练」：原来这里是三行长说明（投诉人员就填人员、
          投诉机构就填机构，两者都投就两块都填；留空表示本案没有投诉这一方。两块至少填一块），
          协会在标注图里把它整段框出来嫌长，压成一句。
          原注释的理由（市民得先知道「留空是允许的、而且是有意思的」，否则会以为两块都必须填、
          随手编一个机构名进去）**仍然成立**，所以不删干净：留空的许可留在这句里，
          「至少填一块」交给块头那枚红色「至少填一项」承担，两处不重复说。 */}
      <p className="rp-note">只投一方就只填一块，另一块留空。</p>

      <div className="rp-line">
        <div className="rp-line-h">
          <h4>{LINE_META.person.title}</h4>
          <span className="rp-line-sub">{LINE_META.person.blankNote}</span>
        </div>
        <DirectoryLine
          kind="person"
          value={
            person
              ? {
                  fromDirectory: person.fromDirectory,
                  name: person.name,
                  code: person.cdNumber,
                  extra: person.orgName,
                }
              : null
          }
          onChange={(v) =>
            onPerson(
              v
                ? {
                    fromDirectory: v.fromDirectory,
                    name: v.name,
                    cdNumber: v.code,
                    // 所属机构原样留在人员这条线上（显示用），**绝不横向流进机构那条线**
                    orgName: v.extra,
                  }
                : null,
            )
          }
        />
      </div>

      <div className="rp-line">
        <div className="rp-line-h">
          <h4>{LINE_META.org.title}</h4>
          <span className="rp-line-sub">{LINE_META.org.blankNote}</span>
        </div>
        <DirectoryLine
          kind="org"
          value={
            org
              ? { fromDirectory: org.fromDirectory, name: org.name, code: org.orgCode, extra: '' }
              : null
          }
          onChange={(v) =>
            onOrg(
              v
                ? { fromDirectory: v.fromDirectory, name: v.name, orgCode: v.code }
                : null,
            )
          }
        />
      </div>
    </div>
  );
}
